import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MatchRow from "@/components/feature/MatchRow";
import { leagues } from "@/mocks/leagues";
import { matches } from "@/mocks/matches";
import { formatDateCN, formatWeekday } from "@/utils/format";

const STATUS_FILTERS = [
  { key: "all", label: "全部" },
  { key: "live", label: "直播中" },
  { key: "upcoming", label: "未开始" },
  { key: "finished", label: "已结束" },
];

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { league: leagueSlug = "" } = useParams();
  const league = leagues.find((l) => l.slug === leagueSlug);
  const leagueMatches = useMemo(
    () => matches.filter((m) => m.leagueSlug === leagueSlug),
    [leagueSlug]
  );

  const dates = useMemo(() => {
    const set = Array.from(new Set(leagueMatches.map((m) => m.date)));
    return set.sort((a, b) => b.localeCompare(a));
  }, [leagueMatches]);

  const [date, setDate] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    return leagueMatches
      .filter((m) => (date === "all" ? true : m.date === date))
      .filter((m) => (status === "all" ? true : m.status === status))
      .sort((a, b) => b.startTime.localeCompare(a.startTime));
  }, [leagueMatches, date, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  if (!league) {
    return (
      <div className="w-full px-4 md:px-6 py-24 text-center">
        <i className="ri-error-warning-line text-5xl text-foreground-400" />
        <h1 className="mt-4 font-heading text-2xl text-foreground-950">未找到该联赛</h1>
        <p className="mt-2 text-foreground-600">请从下方选择一个联赛，或返回首页查看正在进行的直播。</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {leagues.map((l) => (
            <Link
              key={l.slug}
              to={`/category/${l.slug}`}
              className="px-4 py-2 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 hover:border-primary-300 cursor-pointer"
            >
              {l.name}
            </Link>
          ))}
        </div>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800 cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full">
      <section className="relative w-full h-56 md:h-72 overflow-hidden">
        <img
          src={league.banner}
          alt={`${league.fullName} 赛事`}
          title={`${league.name} 篮球直播与赛程 - 篮球直播`}
          className="absolute inset-0 w-full h-full object-top object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/80 to-primary-950/40" />
        <div className="relative h-full w-full px-4 md:px-6 flex flex-col justify-center">
          <nav className="flex items-center gap-1.5 text-xs text-primary-200 mb-3">
            <Link to="/" className="hover:text-accent-400 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span>赛事分类</span>
            <i className="ri-arrow-right-s-line" />
            <span className="text-accent-400">{league.name}</span>
          </nav>
          <h1 className="font-heading font-semibold text-3xl md:text-4xl text-background-50">
            {league.name} 篮球直播与赛程
          </h1>
          <p className="mt-3 max-w-2xl text-primary-100 text-sm md:text-base leading-relaxed">
            {league.intro}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {league.highlights.map((h) => (
              <span
                key={h}
                className="px-3 py-1 rounded-full bg-background-50/10 border border-background-50/20 text-xs text-background-50"
              >
                {h}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-8">
          <aside className="space-y-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="font-heading text-base text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-calendar-line text-accent-600" />
                按日期筛选
              </h2>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setDate("all");
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    date === "all"
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  全部日期
                </button>
                {dates.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => {
                      setDate(d);
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      date === d
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {formatDateCN(d)}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="font-heading text-base text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-filter-3-line text-accent-600" />
                比赛状态
              </h2>
              <div className="flex flex-col gap-2">
                {STATUS_FILTERS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => {
                      setStatus(s.key);
                      setPage(1);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                      status === s.key
                        ? "bg-secondary-100 text-secondary-900 font-medium"
                        : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="font-heading text-base text-foreground-950 mb-3">切换联赛</h2>
              <div className="flex flex-col gap-1.5">
                {leagues.map((l) => (
                  <Link
                    key={l.slug}
                    to={`/category/${l.slug}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                      l.slug === leagueSlug
                        ? "bg-primary-50 text-primary-700 font-medium"
                        : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: l.accent }} />
                    {l.name}
                  </Link>
                ))}
              </div>
            </div>
          </aside>

          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-foreground-600">
                共 <span className="text-foreground-950 font-medium">{filtered.length}</span> 场比赛
                {date !== "all" && ` · ${formatDateCN(date)} ${formatWeekday(date)}`}
              </p>
            </div>

            {pageItems.length === 0 ? (
              <div className="rounded-lg border border-background-200 bg-background-50 py-16 text-center">
                <i className="ri-calendar-close-line text-4xl text-foreground-400" />
                <p className="mt-3 text-foreground-700">没有符合筛选条件的比赛</p>
                <p className="mt-1 text-sm text-foreground-500">
                  试试切换日期或比赛状态，查看更多赛事安排
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setDate("all");
                    setStatus("all");
                    setPage(1);
                  }}
                  className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  重置筛选
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {pageItems.map((m) => (
                  <MatchRow key={m.id} match={m} />
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-2">
                <button
                  type="button"
                  disabled={current <= 1}
                  onClick={() => setPage(current - 1)}
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line" />
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPage(i + 1)}
                    className={`w-9 h-9 rounded-md text-sm transition-colors cursor-pointer ${
                      current === i + 1
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-200 text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={current >= totalPages}
                  onClick={() => setPage(current + 1)}
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}